import { useEffect, useRef, useState } from 'react';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { useLanguage } from '../../shell/language-context';

function reducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// The design's count-up (`[data-n]`): a figure that runs from 0 to its value over 1.4 s
// (cubic ease-out) the first time it is on screen. `loop` is the stats band's variant —
// it holds the figure for 2.2 s, fades it out, and counts again. Reduced motion, and a
// browser without IntersectionObserver, show the value at once. Numbers go through the
// i18n formatter, so Bangla digits and lakh grouping apply (CB-15).
export function Count({ n, loop = false, prefix = '', suffix = '' }: { n: number; loop?: boolean; prefix?: string; suffix?: string }) {
  const { lang } = useLanguage();
  const ref = useRef<HTMLSpanElement>(null);
  const instant = reducedMotion() || typeof IntersectionObserver === 'undefined';
  const [value, setValue] = useState(instant ? n : 0);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || instant) {
      setValue(n);
      return;
    }
    let raf = 0;
    const timers: number[] = [];
    let alive = true;
    const run = () => {
      const t0 = performance.now();
      const step = (t: number) => {
        if (!alive) return;
        const p = Math.min(1, (t - t0) / (loop ? 1600 : 1400));
        setValue(Math.round(n * (1 - (1 - p) ** 3)));
        if (p < 1) raf = requestAnimationFrame(step);
        else if (loop) {
          timers.push(
            window.setTimeout(() => {
              setResetting(true);
              timers.push(
                window.setTimeout(() => {
                  setValue(0);
                  setResetting(false);
                  timers.push(window.setTimeout(run, 250));
                }, 350),
              );
            }, 2200),
          );
        }
      };
      raf = requestAnimationFrame(step);
    };
    const io = new IntersectionObserver((es) => {
      if (es.some((e) => e.isIntersecting)) {
        io.disconnect();
        run();
      }
    });
    io.observe(el);
    return () => {
      alive = false;
      io.disconnect();
      cancelAnimationFrame(raf);
      timers.forEach((id) => clearTimeout(id));
    };
  }, [n, loop, instant]);

  return (
    <>
      <span ref={ref} className={resetting ? 'rs' : undefined} aria-hidden="true">
        {prefix}
        {formatNumber(lang, value)}
        {suffix}
      </span>
      <span className="sr-only">
        {prefix}
        {formatNumber(lang, n)}
        {suffix}
      </span>
    </>
  );
}
